import { z } from 'zod';

import { ModalOfferIdSchema, ModalSessionIdSchema, ModalSessionMoneySchema } from './session';

/** Teto de hotéis que a conclusão do inventário absorve numa sessão de hotel
 *  (spec 2026-09-21-hotel-agent-full-inventory, issue #3449). Acima dele o
 *  estado vira `capped` e o adapter absorve ainda a 1ª página por preço e a 1ª
 *  por avaliações, para superlativos continuarem corretos. */
export const HOTEL_INVENTORY_CAP = 120;

/** `none` = sessão anterior à feature ou flag desligada; `pending` = conclusão
 *  em curso; `superseded` = uma busca nova substituiu a que estava sendo
 *  completada. */
export const ModalSessionHotelInventoryStatusSchema = z.enum([
  'none',
  'pending',
  'complete',
  'capped',
  'failed',
  'superseded',
]);
export type ModalSessionHotelInventoryStatus = z.infer<
  typeof ModalSessionHotelInventoryStatusSchema
>;

/** Uma linha compacta por hotel conhecido: só handle e campos que já viajam no
 *  `modalData` público da oferta. `total` é o TOTAL da estadia (mesma moeda de
 *  `offer.total`); quem fala em diária divide pelas noites. */
export const ModalSessionHotelInventoryHotelSchema = z
  .object({
    offerId: ModalOfferIdSchema,
    title: z.string().min(1),
    total: ModalSessionMoneySchema.optional(),
    rating: z.number().optional(),
    reviewCount: z.number().int().nonnegative().optional(),
    category: z.number().optional(),
    distanceKm: z.number().optional(),
    latitude: z.number().optional(),
    longitude: z.number().optional(),
    policyStatus: z.enum(['within_policy', 'out_of_policy', 'unknown']).optional(),
    breakfast: z.boolean().optional(),
    refundable: z.boolean().optional(),
    /** O AGREGADO do hotel ("tem ALGUMA tarifa com café/reembolsável") — o que o
     *  filtro do cartão lê; `breakfast`/`refundable` acima são da tarifa MAIS
     *  BARATA (#3732). */
    hasBreakfastRate: z.boolean().optional(),
    hasRefundableRate: z.boolean().optional(),
    /** O hotel tem UMA tarifa com café E reembolso (#5849) — café e reembolso
     *  em tarifas separadas não bastam para o corte conjunto. */
    hasBreakfastRefundableRate: z.boolean().optional(),
    /** Se a tarifa mais barata COM o atributo está dentro da política (#5820):
     *  o corte "dentro da política" + café/reembolso exige uma tarifa só que
     *  atenda aos dois. Ausente = desconhecido (não exclui). */
    breakfastRateWithinPolicy: z.boolean().optional(),
    refundableRateWithinPolicy: z.boolean().optional(),
    breakfastRefundableRateWithinPolicy: z.boolean().optional(),
    recommended: z.boolean().optional(),
    agreedRate: z.boolean().optional(),
    favorite: z.boolean().optional(),
    stayedBefore: z.boolean().optional(),
  })
  .strict();
export type ModalSessionHotelInventoryHotel = z.infer<typeof ModalSessionHotelInventoryHotelSchema>;

export const ModalSessionHotelInventoryResponseSchema = z
  .object({
    sessionId: ModalSessionIdSchema,
    status: ModalSessionHotelInventoryStatusSchema,
    /** Contagem da busca inteira (`searchMetadata.count`); ausente quando a
     *  conclusão não a leu. */
    total: z.number().int().nonnegative().optional(),
    /** Quantos hotéis a sessão conhece (`session.offers.length`). `hotels`
     *  abaixo pula ofertas sem `title`, então `hotels.length` pode ser menor
     *  que este número. */
    known: z.number().int().nonnegative(),
    updatedAt: z.iso.datetime().optional(),
    hotels: z.array(ModalSessionHotelInventoryHotelSchema),
  })
  .strict();
export type ModalSessionHotelInventoryResponse = z.infer<
  typeof ModalSessionHotelInventoryResponseSchema
>;
